const { format, isBefore, isSameDay, parse } = require("date-fns");
const { toDate } = require("./dateUtils");

const TIME_FORMAT = "HH:mm";

function parseTimeToMinutes(timeString) {
  const match = String(timeString || "").match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function minutesToTime(totalMinutes) {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function formatTimeForDisplay(timeString) {
  const minutes = parseTimeToMinutes(timeString);
  if (minutes == null) return "";
  return format(parse(minutesToTime(minutes), TIME_FORMAT, new Date()), "h:mm a");
}

function generateFreeformSlots({ startTime = "00:00", endTime = "23:59", intervalMinutes = 30 } = {}) {
  const start = parseTimeToMinutes(startTime);
  const end = parseTimeToMinutes(endTime);
  const interval = Number(intervalMinutes);
  if (start == null || end == null || !Number.isFinite(interval) || interval <= 0) return [];

  const slots = [];
  if (start <= end) {
    for (let minute = start; minute <= end; minute += interval) {
      slots.push(minutesToTime(minute));
    }
    return slots;
  }

  for (let minute = start; minute < 1440; minute += interval) {
    slots.push(minutesToTime(minute));
  }
  for (let minute = 0; minute <= end; minute += interval) {
    slots.push(minutesToTime(minute));
  }
  return slots;
}

function isSlotInPast(dateValue, timeString, nowValue = new Date()) {
  const day = toDate(dateValue);
  const minutes = parseTimeToMinutes(timeString);
  if (!day || minutes == null) return true;

  const slotDate = parse(timeString, TIME_FORMAT, day);
  const now = nowValue instanceof Date ? nowValue : new Date(nowValue);
  if (Number.isNaN(now.getTime())) return false;
  return isSameDay(slotDate, now) && isBefore(slotDate, now);
}

function normalizeSlot(slot) {
  if (typeof slot === "string") return { time: slot, status: "available" };
  return { time: slot?.time || "", status: slot?.status || "available", ...slot };
}

function mergeAvailabilityWithBooked(availableSlots = [], bookedSlots = [], options = {}) {
  const booked = new Set((bookedSlots || []).map((slot) => (typeof slot === "string" ? slot : slot?.time)).filter(Boolean));
  return (availableSlots || [])
    .map(normalizeSlot)
    .filter((slot) => parseTimeToMinutes(slot.time) != null)
    .map((slot) => {
      let status = slot.status || "available";
      if (booked.has(slot.time)) status = "booked";
      if (options.date && isSlotInPast(options.date, slot.time, options.now)) status = "past";
      return {
        ...slot,
        label: formatTimeForDisplay(slot.time),
        status,
      };
    });
}

module.exports = {
  formatTimeForDisplay,
  generateFreeformSlots,
  isSlotInPast,
  mergeAvailabilityWithBooked,
  minutesToTime,
  parseTimeToMinutes,
};
